import React, { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { loadLottieLibrary } from "../../utils/lottieLoader";

const INTRO_URL = `${process.env.PUBLIC_URL}/ai-robot/psa-robot-lottie-intro.json`;
const IDLE_URL = `${process.env.PUBLIC_URL}/ai-robot/psa-robot-lottie-idle.json`;

// Module-level (not component state) so the wave intro plays exactly once per
// page load/SPA session, no matter how many times an avatar mounts (launcher
// + header both render one, and the panel can be opened/closed repeatedly).
// Mirrors the same-named flag in the public-website PSA AI Assistant plugin.
let introPlayed = false;

/**
 * PiA's animated robot mascot — same asset used by the public-website widget
 * and the mobile app's floating assistant button. Paints the static Sparkles
 * icon instantly, then swaps to the Lottie animation once the player (~168KB)
 * and animation data have loaded: a one-shot "wave" intro the first time it
 * appears on a page load, handing off to a looping "idle" animation forever.
 */
const RobotAvatar = ({ size = 28, iconSize, style }) => {
  const containerRef = useRef(null);
  const animRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadLottieLibrary()
      .then((lottie) => {
        if (cancelled || !containerRef.current) return;
        setReady(true);

        const playIdleForever = () => {
          animRef.current = lottie.loadAnimation({
            container: containerRef.current,
            renderer: "svg",
            loop: true,
            autoplay: true,
            path: IDLE_URL,
          });
        };

        if (!introPlayed) {
          const intro = lottie.loadAnimation({
            container: containerRef.current,
            renderer: "svg",
            loop: false, // plays exactly once — "complete" fires right after
            autoplay: true,
            path: INTRO_URL,
          });
          animRef.current = intro;
          intro.addEventListener("complete", () => {
            introPlayed = true;
            intro.destroy();
            if (!cancelled && containerRef.current) playIdleForever();
          });
        } else {
          playIdleForever();
        }
      })
      .catch(() => {
        /* Keep the static Sparkles fallback already in place. */
      });

    return () => {
      cancelled = true;
      try {
        animRef.current?.destroy?.();
      } catch (e) {
        /* no-op */
      }
    };
  }, []);

  return (
    <span
      style={{
        position: "relative",
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        ...style,
      }}
    >
      {!ready && <Sparkles size={iconSize || Math.round(size * 0.5)} strokeWidth={2.25} />}
      <div
        ref={containerRef}
        style={{ width: "100%", height: "100%", display: ready ? "block" : "none" }}
      />
    </span>
  );
};

export default RobotAvatar;
